import type { TranslationPaths } from 'shared/lib/i18n'

/** One selected purchase line — a slot (quantity 1) or a stock quantity. */
export interface PurchaseLine {
	inventoryId: string
	name: string
	quantity: number
}

/** "Black × 3" for a multi-unit line, just the name for a single unit. */
export function formatLine(line: PurchaseLine): string {
	return line.quantity > 1 ? `${line.name} × ${line.quantity}` : line.name
}

export function totalQuantity(lines: PurchaseLine[]): number {
	return lines.reduce((sum, line) => sum + line.quantity, 0)
}

export function purchaseAmountUzs(
	lines: PurchaseLine[],
	unitPriceUzs: number,
): number {
	return unitPriceUzs * totalQuantity(lines)
}

/** How much the wallet is short of the purchase — never negative. */
export function shortfallUzs(amountUzs: number, balanceUzs: number): number {
	return Math.max(0, amountUzs - balanceUzs)
}

/**
 * Backend failures that mean the selection is now stale. The next attempt must
 * carry a different payload, so the idempotency key has to be regenerated —
 * reusing it would clash with the recorded first attempt instead of retrying.
 *
 * `NOT_LOT_INITIATOR` is deliberately absent: the payload is fine, the caller
 * simply is not allowed to buy, so replaying the same key stays correct.
 */
const STALE_SELECTION_CODES = [
	'SLOT_TAKEN',
	'STOCK_CHANGED',
	'PRICE_CHANGED',
	'INSUFFICIENT_FUNDS',
] as const

const MESSAGE_BY_CODE: Record<string, TranslationPaths> = {
	SLOT_TAKEN: 'pages.purchase.slotTaken',
	STOCK_CHANGED: 'pages.purchase.stockChanged',
	PRICE_CHANGED: 'pages.purchase.priceChanged',
	INSUFFICIENT_FUNDS: 'pages.purchase.insufficientFunds',
	NOT_LOT_INITIATOR: 'pages.purchase.notInitiator',
}

export interface PurchaseFailure {
	/** Translation key for the message shown to the buyer. */
	messageKey: TranslationPaths
	/** True when the idempotency key must be replaced before the next attempt. */
	regenerateIdempotencyKey: boolean
}

/**
 * Maps a backend purchase error onto the buyer-facing message and whether the
 * idempotency key is now stale. Unknown errors fall back to the generic message
 * and keep the key, so a network blip replays the same purchase rather than
 * charging the wallet twice.
 */
export function mapPurchaseError(error: unknown): PurchaseFailure {
	const raw = error instanceof Error ? error.message : ''
	const code = Object.keys(MESSAGE_BY_CODE).find((candidate) =>
		raw.includes(candidate),
	)

	if (!code)
		return {
			messageKey: 'pages.purchase.failedMessage',
			regenerateIdempotencyKey: false,
		}

	return {
		messageKey: MESSAGE_BY_CODE[code]!,
		regenerateIdempotencyKey: (
			STALE_SELECTION_CODES as readonly string[]
		).includes(code),
	}
}
